import { useEffect, useState } from "react";
import type { User as SupabaseUser } from "@supabase/supabase-js";

import { supabase } from "@/integrations/supabase/client";
import type { User } from "@/lib/looseend/types";

/**
 * Real application authentication (Lovable Cloud).
 *
 * The immutable Cloud user id is the application identity — never the email
 * address. This module never writes credential material to browser storage,
 * never puts it in a URL, and never hands it to analytics or monitoring.
 *
 * Guest-first access: a visitor without an account is an ANONYMOUS auth user.
 * That identity is real backend identity (it owns a personal workspace and is
 * subject to the same RLS as everyone else); it is simply not durable across
 * devices. `User.isGuest` is how the UI tells the two apart.
 */

/** Projects a Cloud user onto the app-facing identity. `id` is immutable. */
export function mapCloudUser(user: SupabaseUser): User {
  const metadata = (user.user_metadata ?? {}) as { full_name?: string; name?: string };
  const isGuest = user.is_anonymous === true;
  const email = user.email ?? "";
  const name =
    metadata.full_name ||
    metadata.name ||
    (email ? (email.split("@")[0] ?? "") : "") ||
    (isGuest ? "Guest" : "You");
  return {
    id: user.id,
    name,
    email,
    createdAt: user.created_at ?? new Date().toISOString(),
    isGuest,
  };
}

/** Only same-origin app paths may be restored after sign-in. */
export function sanitizeRedirect(value: unknown, fallback = "/app"): string {
  if (typeof value !== "string" || !value.startsWith("/") || value.startsWith("//"))
    return fallback;
  if (value.startsWith("/auth")) return fallback;
  return value;
}

export type AuthStatus = "loading" | "authenticated" | "unauthenticated";

export interface AuthSession {
  status: AuthStatus;
  user: User | null;
}

/** Re-validates identity with the auth server, then tracks session changes. */
export function useAuthSession(): AuthSession {
  const [session, setSession] = useState<AuthSession>({ status: "loading", user: null });

  useEffect(() => {
    let active = true;

    const resolve = async () => {
      const { data, error } = await supabase.auth.getUser();
      if (!active) return;
      setSession(
        error || !data.user
          ? { status: "unauthenticated", user: null }
          : { status: "authenticated", user: mapCloudUser(data.user) },
      );
    };

    void resolve();

    const { data: subscription } = supabase.auth.onAuthStateChange((event) => {
      if (event !== "SIGNED_IN" && event !== "SIGNED_OUT" && event !== "USER_UPDATED") return;
      if (event === "SIGNED_OUT") {
        setSession({ status: "unauthenticated", user: null });
        return;
      }
      void resolve();
    });

    return () => {
      active = false;
      subscription.subscription.unsubscribe();
    };
  }, []);

  return session;
}

/**
 * Establishes a guest (anonymous) backend session when no session exists.
 * Idempotent: an existing session of any kind is left untouched. Resolves
 * false when the auth server refused; the caller shows a safe retry state.
 */
export async function ensureGuestSession(): Promise<boolean> {
  const { data } = await supabase.auth.getSession();
  if (data.session) return true;
  const { error } = await supabase.auth.signInAnonymously();
  return !error;
}

/** True when the CURRENT browser session belongs to an anonymous guest. */
export async function currentSessionIsGuest(): Promise<boolean> {
  const { data } = await supabase.auth.getSession();
  return data.session?.user.is_anonymous === true;
}

export async function signInWithEmail(email: string, password: string) {
  const { error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
}

export class EmailAlreadyRegisteredError extends Error {
  constructor() {
    super("email_already_registered");
    this.name = "EmailAlreadyRegisteredError";
  }
}

/**
 * Creates an account.
 *
 * From a GUEST session this is an in-place identity upgrade: the anonymous
 * user gains an email and password and keeps the SAME user id, so the guest's
 * workspace and conversations are the account's without any data movement.
 * If the email already belongs to an account the upgrade is refused and the
 * caller offers sign-in instead (which then claims the guest history).
 */
export async function signUpWithEmail(email: string, password: string) {
  if (await currentSessionIsGuest()) {
    const { data, error } = await supabase.auth.updateUser({ email, password });
    if (error) {
      if (
        error.code === "email_exists" ||
        error.code === "user_already_exists" ||
        /already/i.test(error.message)
      )
        throw new EmailAlreadyRegisteredError();
      throw error;
    }
    return { needsConfirmation: !data.user.email_confirmed_at, upgraded: true as const };
  }

  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: { emailRedirectTo: `${window.location.origin}/auth` },
  });
  if (error) {
    if (error.code === "user_already_exists") throw new EmailAlreadyRegisteredError();
    throw error;
  }
  return { needsConfirmation: data.session === null, upgraded: false as const };
}

export async function signOutApplicationSession() {
  await supabase.auth.signOut();
}
